import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isTrustedOrigin } from '../utils/security';

export const openNotifyModal = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('open-notify-modal'));
  }
};

export default function NotifyMeModal() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(500);

  const isFrench = i18n.language?.startsWith('fr');
  const formUrl = `https://forms.zohopublic.ca/elloAssurances/form/NotifyMe/formperma/MUV0eZcMXnEA_VkSPETjkFpYGESmO_ucyPEFqLEpYSA?zf_rszfm=1${isFrench ? '&zf_lang=fr' : ''}`;

  useEffect(() => {
    const handleOpen = () => {
      setIsOpen(true);
      document.body.style.overflow = 'hidden';
    };

    window.addEventListener('open-notify-modal', handleOpen);
    return () => window.removeEventListener('open-notify-modal', handleOpen);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Zoho Form Auto-Resize postMessage Handler
  useEffect(() => {
    const handleMessage = (event) => {
      if (!isTrustedOrigin(event.origin)) return;

      const evntData = event.data;
      if (typeof evntData === 'string') {
        const parts = evntData.split('|');
        if (parts.length >= 2) {
          const [perma, heightStr] = parts;
          if (perma === 'MUV0eZcMXnEA_VkSPETjkFpYGESmO_ucyPEFqLEpYSA') {
            const height = parseInt(heightStr, 10);
            if (!isNaN(height) && height > 50) {
              setIframeHeight(height);
            }
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-4 md:p-6 animate-fade-in">
      {/* Blurred Transparent Backdrop */}
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-md transition-opacity duration-300"
        onClick={handleClose}
      />

      {/* Modal Dialog Card (Wide horizontal & compact vertical) */}
      <div className="relative w-full max-w-2xl md:max-w-3xl lg:max-w-4xl bg-white rounded-3xl md:rounded-[2rem] shadow-[0_32px_120px_-20px_rgba(0,0,0,0.6)] border border-outline-variant/10 overflow-hidden z-10 animate-zoom-in flex flex-col max-h-[85vh]">
        
        {/* Floating Close 'X' Button */}
        <button
          onClick={handleClose}
          aria-label="Close modal"
          className="absolute top-3 right-3 sm:top-3.5 sm:right-4 z-30 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-slate-100/90 hover:bg-slate-200 text-slate-700 flex items-center justify-center shadow-sm hover:rotate-90 hover:scale-110 active:scale-95 transition-all duration-300 border border-slate-200/80"
        >
          <span className="material-symbols-outlined text-base sm:text-lg font-bold">close</span>
        </button>

        {/* Modal Header (Compact) */}
        <div className="py-3 sm:py-3.5 px-5 sm:px-8 bg-gradient-to-b from-slate-50 to-white border-b border-slate-100 flex items-center gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 shadow-sm">
            <span className="material-symbols-outlined text-lg sm:text-xl">notifications_active</span>
          </div>
          <div className="pr-10">
            <h3 className="font-headline text-base sm:text-lg font-extrabold text-slate-900 leading-snug">
              {isFrench ? "Soyez averti du lancement" : "Get Notified on Launch"}
            </h3>
            <p className="text-[10px] sm:text-xs text-slate-500">
              {isFrench 
                ? "Inscrivez-vous pour recevoir un accès prioritaire dès la disponibilité."
                : "Sign up to receive priority access as soon as coverage becomes available."}
            </p>
          </div>
        </div>

        {/* Scrollable Iframe Container (Spacious horizontal, compact vertical) */}
        <div className="w-full bg-[#f8f9fa] overflow-y-auto custom-scrollbar flex-1 px-4 sm:px-8 py-2 sm:py-3">
          <iframe
            src={formUrl}
            className="block w-full border-0 transition-all duration-300"
            style={{
              height: `${iframeHeight}px`,
              minHeight: '340px'
            }}
            title={isFrench ? "M'avertir" : "Notify Me"}
            scrolling="no"
          />
        </div>
      </div>
    </div>
  );
}
